/**
 * The one device-status vocabulary for the console.
 *
 * Every page shows a device's connection from the server-derived
 * `connection` fact (live / offline / never_connected), so a device never
 * reads "Streaming" on one page and "Unavailable" on another.
 */
import type { StatusState } from '@/components/ui/statusSemantics';
import type { OperationsConnectionState, OperationsFleetDevice } from '@/types/operations';
import { formatRelativeTime } from '@/utils/relativeTime';

export interface DeviceConnectionPresentation {
  state: OperationsConnectionState;
  /** Short status word for pills and headers. */
  label: string;
  badge: StatusState;
  /** One plain sentence with the time context. */
  detail: string;
}

const heardAtFormat = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatHeardAt(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${heardAtFormat.format(parsed)} (${formatRelativeTime(parsed)})`;
}

export function describeDeviceConnection(
  record: Pick<OperationsFleetDevice, 'connection' | 'client_contact'>,
): DeviceConnectionPresentation {
  const heardAt = formatHeardAt(record.client_contact.last_seen);
  switch (record.connection) {
    case 'live':
      return {
        state: 'live',
        label: 'Live',
        badge: 'ready',
        detail: 'Audio is arriving now.',
      };
    case 'offline':
      return {
        state: 'offline',
        label: 'Offline',
        badge: 'blocked',
        detail: heardAt ? `Last heard ${heardAt}.` : 'No audio is arriving.',
      };
    case 'never_connected':
      return {
        state: 'never_connected',
        label: 'Not connected yet',
        badge: 'unavailable',
        detail: 'The device has never sent audio.',
      };
  }
}
